import React, { useState } from 'react';
import { Button, Text, Textarea } from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../../theme/fluent';
import type { ChatWidgetProps } from '../../types/widgets';
import type { ApiConfirmWidget } from '../../types/api';

export const ConfirmWidget: React.FC<ChatWidgetProps<ApiConfirmWidget>> = ({
  data,
  isDisabled = false,
  onAction,
}) => {
  const { tokens, modeTokens } = useFluentTokens();
  const { widget_id, title, description, options, allow_feedback, feedback_prompt } = data;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [clickedOptionId, setClickedOptionId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [pendingOption, setPendingOption] = useState<string | null>(null);

  const submitAction = async (optionId: string, feedbackText?: string) => {
    if (!onAction) return;
    setIsSubmitting(true);
    setClickedOptionId(optionId);
    try {
      await Promise.resolve(onAction('button_action', {
        widget_id,
        button_action: optionId,
        selected_options: feedbackText ? [feedbackText] : [],
      }));
      setHasSubmitted(true);
    } finally {
      setIsSubmitting(false);
      setClickedOptionId(null);
    }
  };

  const handleOptionClick = async (optionId: string) => {
    if (isDisabled || hasSubmitted) return;

    // For non-approve actions with allow_feedback, show the feedback input
    if (allow_feedback && optionId !== 'approve') {
      setPendingOption(optionId);
      setShowFeedback(true);
      return;
    }

    await submitAction(optionId);
  };

  const handleFeedbackSubmit = async () => {
    if (!pendingOption) return;
    await submitAction(pendingOption, feedback.trim() || undefined);
  };

  const effectivelyDisabled = isDisabled || hasSubmitted;

  const getButtonAppearance = (style?: string): 'primary' | 'secondary' => {
    switch (style) {
      case 'success':
      case 'primary':
      case 'danger':
        return 'primary';
      default:
        return 'secondary';
    }
  };

  const getButtonColor = (style?: string): React.CSSProperties => {
    if (effectivelyDisabled) return {};
    if (style === 'success') {
      return { backgroundColor: modeTokens.colorBrand80, color: '#ffffff', border: 'none' };
    }
    if (style === 'danger') {
      return { backgroundColor: modeTokens.colorPaletteRedBackground3, color: '#ffffff', border: 'none' };
    }
    return {};
  };

  return (
    <Box
      style={{
        marginTop: tokens.spacingVerticalM,
        padding: tokens.spacingHorizontalL,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: modeTokens.colorNeutralBackground2,
        border: `0.5px solid ${modeTokens.colorNeutralStroke2}`,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
      }}
    >
      <Text
        as="h3"
        size={500}
        weight="semibold"
        style={{ display: 'block', color: modeTokens.colorNeutralForeground1 }}
      >
        {title}
      </Text>

      {description && (
        <Text
          size={300}
          style={{ display: 'block', color: modeTokens.colorNeutralForeground2 }}
        >
          {description}
        </Text>
      )}

      {showFeedback && !hasSubmitted && (
        <Box style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
          <Textarea
            placeholder={feedback_prompt || 'Any feedback? (optional)'}
            value={feedback}
            onChange={(_, d) => setFeedback(d.value)}
            disabled={isSubmitting}
            resize="vertical"
            style={{ minHeight: '60px' }}
          />
          <Box style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
            <Button
              appearance="primary"
              size="small"
              disabled={isSubmitting}
              onClick={handleFeedbackSubmit}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
            <Button
              appearance="secondary"
              size="small"
              disabled={isSubmitting}
              onClick={() => { setShowFeedback(false); setPendingOption(null); }}
            >
              Back
            </Button>
          </Box>
        </Box>
      )}

      {!showFeedback && (
        <Box
          style={{
            display: 'flex',
            gap: tokens.spacingHorizontalM,
            flexWrap: 'wrap',
          }}
        >
          {options.map((option) => (
            <Button
              key={option.id}
              appearance={getButtonAppearance(option.style)}
              disabled={effectivelyDisabled || isSubmitting}
              onClick={() => handleOptionClick(option.id)}
              style={{
                borderRadius: tokens.borderRadiusXXXXLarge,
                fontWeight: tokens.fontWeightSemibold,
                fontSize: tokens.fontSizeBase300,
                lineHeight: tokens.lineHeightBase300,
                paddingLeft: tokens.spacingHorizontalM,
                paddingRight: tokens.spacingHorizontalM,
                paddingTop: tokens.spacingVerticalXS,
                paddingBottom: tokens.spacingVerticalXS,
                minWidth: '120px',
                ...getButtonColor(option.style),
                ...(effectivelyDisabled ? { cursor: 'not-allowed' } : {}),
              }}
            >
              {clickedOptionId === option.id && isSubmitting ? 'Submitting...' : option.label}
            </Button>
          ))}
        </Box>
      )}
    </Box>
  );
};
